<template>
    <div class="c-network-map">
        <div class="u-container u-grid c-network-map__container">

            <div class="c-network-map__filter">

                <DisciplineFilterSelect v-for="filterType in filterTypes" :key="filterType.key" :filterType="filterType"
                    v-model:selectedFilters="selectedFilters[filterType.key]" v-show="filterType.show" />

            </div>

            <LocationModal v-if="selectedMarker" :marker="selectedMarker" :selected-filters="selectedFilters"
                @close="deselectMarker" v-model:selectedMarker="selectedMarker" />

        </div>

        <MapboxMap
            access-token="pk.eyJ1Ijoic2VwdGVtYmVyZGlnaXRhbCIsImEiOiJjbHl6cWdsazYyM21hMmpxc2gzcnAyYjF1In0.bmYBczXp97GPpFlxyEXDuA"
            map-style="mapbox://styles/septemberdigital/cmluxh5gj003v01sl7r1l9tbi" :center="mapCenter" :zoom="mapZoom"
            :pitchWithRotate="false" :dragRotate="false" :scrollZoom="false"
            @mb-created="(mapInstance) => mapRef = mapInstance" @mb-drag="deselectMarker">

            <MapboxNavigationControl position="bottom-right" :showCompass="false" />

            <MapboxMarker v-for="marker in filteredMarkers" :key="marker.id" :lng-lat="[marker.lng, marker.lat]">
                <div @click="markerClick(marker)" :data-id="marker.id"
                    :class="{ 'is-selected': selectedMarker && selectedMarker.id === marker.id }">
                    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="29.594"
                        height="48.604" viewBox="0 0 29.594 48.604">
                        <defs>
                            <filter id="Union_7" x="0" y="0" width="29.594" height="48.604"
                                filterUnits="userSpaceOnUse">
                                <feOffset dy="3" input="SourceAlpha" />
                                <feGaussianBlur stdDeviation="2.5" result="blur" />
                                <feFlood flood-opacity="0.302" />
                                <feComposite operator="in" in2="blur" />
                                <feComposite in="SourceGraphic" />
                            </filter>
                        </defs>
                        <g transform="translate(8 5)">
                            <g transform="matrix(1, 0, 0, 1, -8, -5)" filter="url(#Union_7)">
                                <path id="Union_7-2" data-name="Union 7"
                                    d="M17481.615,9823.415a1.5,1.5,0,0,1,1.5-1.5v-16.01a6.8,6.8,0,1,1,4.807-1.992,6.778,6.778,0,0,1-4.807,1.992v16.01a1.5,1.5,0,1,1-1.5,1.5Z"
                                    transform="translate(-17468.32 -9787.31)" fill="#00127A" stroke="#00127A"
                                    stroke-width="1" />
                            </g>
                        </g>
                    </svg>
                </div>
            </MapboxMarker>

        </MapboxMap>
    </div>

</template>

<script setup>
import { ref, computed } from 'vue';
import { MapboxMap, MapboxMarker, MapboxNavigationControl } from '@studiometa/vue-mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import DisciplineFilterSelect from './DisciplineFilterSelect.vue';
import LocationModal from './LocationModal.vue';

const props = defineProps({
    markers: {
        type: Array,
        default: [],
    },
    filterTypes: {
        type: Array,
        default: [],
    },
    selectedFilters: {
        type: Object,
        default: {},
    },
});

const breakpoint = window.matchMedia('(max-width:767px)');
const markers = ref(props.markers);
const filterTypes = ref(props.filterTypes);
const selectedFilters = ref(props.selectedFilters);
const selectedMarker = ref(null);

const mapRef = ref(null);
const mapCenter = ref([5.2913, 52.1326]); // Center of the Netherlands
const mapZoom = ref(6.5);

const extractMarkerDisciplines = (marker) => {
    if (Array.isArray(marker.terms) && marker.terms.length) return marker.terms;

    const terms = [];
    if (Array.isArray(marker.courses)) {
        marker.courses.forEach((course) => {
            const discs = course.discipline;
            if (Array.isArray(discs)) {
                discs.forEach((d) => {
                    if (d && d.slug) terms.push({ taxonomy: 'discipline', slug: d.slug });
                });
            }
        });
    }
    return terms;
};

const filteredMarkers = computed(() => {
    return markers.value.filter((marker) => {
        if (!Array.isArray(marker.courses) || marker.courses.length === 0) {
            return false;
        }

        return Object.keys(selectedFilters.value).every((taxonomy) => {
            const selected = selectedFilters.value[taxonomy];

            if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                return true;
            }

            const markerTerms = extractMarkerDisciplines(marker);

            return markerTerms.some((term) => {
                if (term.taxonomy !== taxonomy) return false;
                if (typeof term.slug === 'string') return term.slug === selected;
                if (Array.isArray(term.slug)) return term.slug.includes(selected);
                return false;
            });
        });
    });
});

const deselectMarker = () => {
    selectedMarker.value = null;
}

const markerClick = (marker) => {
    selectedMarker.value = marker;
    if (!breakpoint.matches) {
        mapRef.value.flyTo({ center: [marker.lng + 1, marker.lat], zoom: 7 });
    } else {
        mapRef.value.flyTo({ center: [marker.lng, marker.lat - 0.5], zoom: 7 });
    }
}

</script>

<style scoped></style>
